const API_BASE_URL = 'https://inza-backend.sajholding.org/api/v1';

class MembershipService {
  async getMemberships() {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/memberships/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching memberships:', error);
      throw error;
    }
  }

  async checkMembership(type = null) {
    try {
      const token = localStorage.getItem('access_token');
      const url = type ? `${API_BASE_URL}/memberships/check/?type=${type}` : `${API_BASE_URL}/memberships/check/`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking membership:', error);
      throw error;
    }
  }

  async hasActiveMembership(type) {
    try {
      const result = await this.checkMembership(type);
      return result.has_active_membership;
    } catch (error) {
      console.error('Error checking active membership:', error);
      return false;
    }
  }

  async getActiveMemberships() {
    try {
      const result = await this.getMemberships();
      return result.memberships.filter(m => m.is_active);
    } catch (error) {
      console.error('Error getting active memberships:', error);
      return [];
    }
  }

  // Vérifie si l'utilisateur a déjà un abonnement de type spécifique
  async hasMembershipType(type) {
    try {
      const memberships = await this.getActiveMemberships();
      return memberships.some(m => m.type === type);
    } catch (error) {
      console.error('Error checking membership type:', error);
      return false;
    }
  }
}

class FlashOfferUsageService {
  async getFlashOfferUsages() {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/flash-offers/usage/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching flash offer usages:', error);
      throw error;
    }
  }

  async checkFlashOfferUsage(offerId) {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/flash-offers/${offerId}/check-usage/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking flash offer usage:', error);
      throw error;
    }
  }

  async hasUsedFlashOffer(offerId) {
    try {
      const result = await this.checkFlashOfferUsage(offerId);
      return result.has_used;
    } catch (error) {
      console.error('Error checking if flash offer was used:', error);
      return false;
    }
  }

  async getUsedFlashOffers() {
    try {
      const result = await this.getFlashOfferUsages();
      return result.usages;
    } catch (error) {
      console.error('Error getting used flash offers:', error);
      return [];
    }
  }

  // Vérifie si l'utilisateur a déjà utilisé une offre flash spécifique
  async isFlashOfferUsed(offerId) {
    try {
      const usedOffers = await this.getUsedFlashOffers();
      return usedOffers.some(usage => usage.flash_offer.id === offerId);
    } catch (error) {
      console.error('Error checking if flash offer is used:', error);
      return false;
    }
  }
}

// Service combiné pour vérifier toutes les restrictions d'offres
class OfferRestrictionService {
  constructor() {
    this.membershipService = new MembershipService();
    this.flashOfferUsageService = new FlashOfferUsageService();
  }

  // Vérifie si une offre est disponible pour l'utilisateur
  async checkOfferAvailability(offer) {
    const restrictions = {
      isAvailable: true,
      reason: null,
      alreadySubscribed: false,
      alreadyUsed: false
    };

    try {
      // Vérifier les abonnements Club INZA
      if (offer.membership === 'lite' || offer.membership === 'premium') {
        const hasMembership = await this.membershipService.hasMembershipType(offer.membership);
        if (hasMembership) {
          restrictions.isAvailable = false;
          restrictions.reason = `Vous êtes déjà abonné au Club INZA ${offer.membership === 'lite' ? 'Lite' : 'Premium'}`;
          restrictions.alreadySubscribed = true;
        }
      }

      // Vérifier les offres flash
      if (offer.membership === 'flash' && offer.id) {
        const hasUsed = await this.flashOfferUsageService.hasUsedFlashOffer(offer.id);
        if (hasUsed) {
          restrictions.isAvailable = false;
          restrictions.reason = 'Vous avez déjà utilisé cette offre flash';
          restrictions.alreadyUsed = true;
        }
      }

    } catch (error) {
      console.error('Error checking offer availability:', error);
      // En cas d'erreur, on considère que l'offre est disponible
      restrictions.isAvailable = true;
      restrictions.reason = null;
    }

    return restrictions;
  }

  // Vérifie plusieurs offres en parallèle
  async checkMultipleOffersAvailability(offers) {
    const checks = offers.map(offer => this.checkOfferAvailability(offer));
    const results = await Promise.allSettled(checks);
    
    return offers.map((offer, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        return {
          offerId: offer.id,
          ...result.value
        };
      } else {
        return {
          offerId: offer.id,
          isAvailable: true,
          reason: null,
          alreadySubscribed: false,
          alreadyUsed: false,
          error: result.reason
        };
      }
    });
  }
}

const membershipService = new MembershipService();
const flashOfferUsageService = new FlashOfferUsageService();
const offerRestrictionService = new OfferRestrictionService();

export {
  membershipService,
  flashOfferUsageService,
  offerRestrictionService
};

export default offerRestrictionService;
